import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import React, { Fragment, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import WithLogin from '../withLogin.component';
import { fetchProductList, deleteProductById } from '../../services/product.service';
import AddProduct from './productAdd.component'
import ViewProduct from './productView.component'
import EditProduct from './productEdit.component'
import DeleteIcon from '@mui/icons-material/Delete';
import { customTableStyles, dateTimeFormat } from '../../utils';
import MessageContainer from '../message.component';
import DataTable from 'react-data-table-component';
import DeleteConfirmation from '../deleteConfirmation.component';

const Product = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [openAddForm, setOpenAddForm] = useState(false);
    const [openEditForm, setOpenEditForm] = useState(false);
    const [openViewForm, setOpenViewForm] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [message, setMessage] = useState({});

    const [partnerSearchFilters, setPartnerSearchFilters] = useState({
        searchText: '',
        adminId: '',
    });

    const columns = [
        {
            name: 'ID',
            selector: row => row.id,
            width: '10%',
        },
        {
            name: 'Product Name',
            selector: row => `${row.name}`,
        },
        {
            name: 'Product Sku',
            selector: row => row.sku,
        },
        {
            name: 'Unit Price',
            selector: row => row.unit_price,
        },
        {
            name: 'Product Type',
            selector: row => row.product_type,
        },
        {
            name: 'Created at',
            selector: row => dateTimeFormat(row.created_at),
        },
        {
            key: 'action',
            text: 'Action',
            name: 'Action',
            className: 'action',
            width: '100px',
            align: 'left',
            sortable: false,
            cell: row => {
                return (
                    <Fragment>
                        <span className="table-action-icon">
                            <EditIcon
                                className="icon-color"
                                onClick={() => {
                                    onEdit(row);
                                }}
                            />
                        </span>
                        &nbsp;
                        <span className="table-action-icon">
                            <VisibilityIcon className="icon-color"
                                onClick={() => {
                                    onView(row);
                                }}
                            />
                        </span>
                        {!row.is_kit_order_present &&
                            <span className="table-action-icon">
                                <DeleteIcon
                                    color="red"
                                    className="table-action-icon red"
                                    onClick={() => onDelete(row)}
                                />
                            </span>
                        }
                    </Fragment>
                );
            },
        },
    ];

    const onDelete = async row => {
        setShowDeleteConfirmation(true);
        setSelectedRow(row);
    };

    const onEdit = row => {
        setSelectedRow(row);
        setOpenEditForm(true);
    };

    const onView = row => {
        setOpenViewForm(true);
        setSelectedRow(row);
    };

    const handleOpenAddForm = () => {
        setOpenAddForm(true);
    };

    const getProducts = async (toPage, toPerPage) => {
        setLoading(true);
        setMessage({});
        toPage = toPage ? toPage : page;
        toPerPage = toPerPage ? toPerPage : perPage;
        try {
            const partners = await fetchProductList(
                toPage,
                toPerPage,
                partnerSearchFilters,
            );
            setData(partners.items);
            setTotalRows(partners.total);
        } catch (error) {
            setMessage({
                detail: 'An error has occurrded during data loading.',
                type: 'Error',
            });
        }
        setLoading(false);
    };

    const deleteProduct = async id => {
        setMessage({});
        try {
            const resp = await deleteProductById(id);
            reloadDataTable(page);
            setMessage({
                type: 'Success',
                message: `${resp.detail}`,
            });
        } catch (error) {
            if (
                error.response.status === 400 ||
                error.response.status === 404
            ) {
                setMessage({
                    type: 'Error',
                    message: `${error.response.data.detail}`,
                });
            }
        }
        setShowDeleteConfirmation(false);
    };

    useEffect(() => {
        getProducts(1);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePageChange = toPage => {
        getProducts(toPage);
        setPage(toPage);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        await getProducts(page, newPerPage);
        setPerPage(newPerPage);
    };

    const { register, handleSubmit, reset } = useForm({
        defaultValues: {},
    });

    useEffect(() => {
        getProducts(1);
        setPage(1);
    }, [partnerSearchFilters]); // eslint-disable-line react-hooks/exhaustive-deps


    const setSearchFilters = async data => {
        setPartnerSearchFilters({
            searchText: data.searchText.trim(),
        });
    };

    const reloadDataTable = toPage => {
        getProducts(toPage);
        if (toPage) {
            setPage(toPage);
        }
    };

    return (
        <>
            {openAddForm && (
                <AddProduct
                    open={openAddForm}
                    setOpenAddForm={setOpenAddForm}
                    reloadDataTable={reloadDataTable}
                />
            )}
            {openEditForm && (
                <EditProduct
                    open={openEditForm}
                    setEditForm={setOpenEditForm}
                    selectedRow={selectedRow}
                    reloadDataTable={reloadDataTable}
                />
            )}
            {openViewForm && (
                <ViewProduct
                    open={openViewForm}
                    setEditForm={setOpenViewForm}
                    selectedRow={selectedRow}
                    reloadDataTable={reloadDataTable}
                />
            )}
            <div>
                <div className="pageHeader">
                    <span id="header">
                        <h4>Products</h4>
                    </span>
                    <span id="header-add-btn">
                        <button
                            className="btn btn-primary"
                            onClick={handleOpenAddForm}>
                            + Add Product
                        </button>
                    </span>
                </div>

                <hr />

                {message && <MessageContainer message={message} />}
                <form className="row g-2" onSubmit={handleSubmit(setSearchFilters)}>
                    <div className="col-md-3">
                        <input
                            type="text"
                            className="form-control"
                            id="search"
                            placeholder="Enter product's sku or name"
                            {...register('searchText')}
                        />
                    </div>
                    <div className="col-md-9">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}>
                            {loading && (
                                <span className="spinner-border spinner-border-sm"></span>
                            )}
                            Search
                        </button>
                        <span className="mx-1"></span>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                            onClick={() => {
                                reset();
                            }}>
                            Reset
                        </button>
                    </div>
                </form>
                <div className="new-line"></div>
                <div className="admin-list-table">
                    <DataTable
                        width="100%"
                        customStyles={customTableStyles}
                        columns={columns}
                        data={data}
                        progressPending={loading}
                        pagination
                        paginationServer
                        paginationDefaultPage={page}
                        paginationResetDefaultPage={true}
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                    />
                </div>
                <DeleteConfirmation
                    showModal={showDeleteConfirmation}
                    hideModal={() => setShowDeleteConfirmation(false)}
                    confirmModal={deleteProduct}
                    deleteId={selectedRow.id}
                    message="Are you sure you want to delete it"
                />
            </div>
        </>
    )
}

export default WithLogin(Product);